import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { Panel, StageBadge } from "@/components/tam/ui";

const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });

function Group({ label, items, tone, testId }) {
  if (!items.length) return null;
  return (
    <div className="mb-5 last:mb-0" data-testid={testId}>
      <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${tone}`}>{label} ({items.length})</p>
      <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md">
        {items.map((a) => (
          <li key={a.id}>
            <Link to={`/accounts/${a.id}`} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`followup-${a.acc_no}`}>
              <span className="font-mono text-xs w-24 shrink-0">{fmt(a.next_follow_up)}</span>
              <span className="flex-1 min-w-0">
                <span className="font-medium text-[#0B132B]">{a.name}</span>
                <span className="block text-xs text-slate-500 truncate">
                  {[a.contact?.contact_name, a.contact?.tel].filter(Boolean).join(" · ")}{a.follow_up_note ? ` — ${a.follow_up_note}` : ""}
                </span>
              </span>
              <StageBadge stage={a.stage} />
              <span className="text-xs text-slate-500 w-32 shrink-0 sm:text-right">{a.follow_up_by_name}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function FollowUps() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/followups").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const empty = !d.overdue.length && !d.due_today.length && !d.upcoming.length;
  return (
    <Panel title={<span className="flex items-center gap-2"><CalendarClock size={17} className="text-[#D9232A]" /> Follow-up reminders</span>}
      className="mb-6" testId="followups-panel"
      action={<span className="text-xs text-slate-500" data-testid="followups-due-count">{d.overdue.length + d.due_today.length} due now</span>}>
      {empty && <p className="text-sm text-slate-500" data-testid="followups-empty">No follow-ups scheduled. Add a follow-up date when logging a call or email.</p>}
      <Group label="Overdue" items={d.overdue} tone="text-[#D9232A]" testId="followups-overdue" />
      <Group label="Due today" items={d.due_today} tone="text-amber-700" testId="followups-today" />
      <Group label="Next 7 days" items={d.upcoming} tone="text-slate-500" testId="followups-upcoming" />
    </Panel>
  );
}
